import React, { useCallback, useMemo, useState } from 'react';

import Decimal from 'decimal.js';
import debounce from 'lodash/debounce';
import { di } from 'react-magnetic-di';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled';
import Textfield from '@atlaskit/textfield/text-field';

import { asNumberValue } from '../../../../../../../../common/types/form/form-fields/field-values/utils';
import type { FormFieldComponentRendererProps } from '../../../../../../../../common/types/renderers';
import {
	useCurrentValue,
	useFieldInvalid,
	useFieldRequired,
	useFormRendererActions,
} from '../../../../../../../../controllers/form-renderer-store';

/**
 * Sanitises raw input into an allowed numeric string. Mirrors what the user is
 * permitted to type so the controlled input keeps a clean value rather than
 * rejecting keystrokes:
 *  - strips any character that is not a digit, decimal point or hyphen;
 *  - removes hyphens that are not at the very start (so `-` is a leading sign
 *    only);
 *  - keeps only the first decimal point, removing any subsequent ones.
 *
 * The last step intentionally uses string slicing rather than a lookbehind
 * regex because lookbehind is disallowed by the `no-lookahead-lookbehind-regexp`
 * lint rule (for cross-browser compatibility).
 */
const sanitiseNumberInput = (rawValue: string): string => {
	const withAllowedCharacters = rawValue
		.trim()
		.replace(/[^0-9.-]/g, '') // only allow numbers, periods, and hyphens
		.replace(/(?!^)-/g, ''); // remove hyphens that are not at the start
	const firstDecimalPointIndex = withAllowedCharacters.indexOf('.');
	if (firstDecimalPointIndex === -1) {
		return withAllowedCharacters;
	}
	// Keep everything up to and including the first decimal point, then strip any
	// remaining decimal points from the rest.
	return (
		withAllowedCharacters.slice(0, firstDecimalPointIndex + 1) +
		withAllowedCharacters.slice(firstDecimalPointIndex + 1).replace(/\./g, '')
	);
};

/**
 * Leading-only, value-less states the user must be able to type before a full
 * number exists (e.g. the start of a negative or decimal number). `decimal.js`
 * throws on these, so they are handled explicitly: echoed to local state so the
 * field stays editable, but never pushed to shared state because they have no
 * numeric value yet.
 */
const ALLOWED_PREFIXES = new Set(['-', '.', '-.']);

/**
 * Determines whether the input string is a number that JavaScript can represent
 * without losing precision.
 *
 * `parseFloat` silently clamps numbers with more significant digits than a
 * double can hold to the nearest representable value — e.g.
 * `parseFloat('12345678901234567890')` returns `12345678901234567000`. Storing
 * that clamped value would make local and shared state disagree with what the
 * user typed, so we treat such inputs as unrepresentable.
 *
 * `decimal.js` parses the full-precision value from the string, so comparing it
 * against the `parseFloat` result detects any precision loss. Purely cosmetic
 * differences that do NOT change the value are still representable, so formatted
 * inputs like `1.`, `1.0`, `1.50`, `.5`, `01`, `-0` and exact small decimals
 * like `0.0000001` remain valid. Values with more significant digits than a
 * double can represent (e.g. `12345678901234567890`) are not representable.
 */
const isRepresentableNumber = (value: string): boolean => {
	try {
		const exact = new Decimal(value);
		return exact.isFinite() && exact.eq(new Decimal(parseFloat(value)));
	} catch {
		return false;
	}
};

export const EditNumberInput = ({
	fieldId,
	fieldInputId,
	fieldErrorId,
}: FormFieldComponentRendererProps): React.JSX.Element => {
	di(useCurrentValue);
	const isInvalid = useFieldInvalid({ fieldId });
	const currentValue = useCurrentValue({ fieldId });
	const [numberFieldValue, setNumberFieldValue] = useState(
		currentValue ? asNumberValue(currentValue)?.number.toString() : '',
	);
	const { updateModifiedValue } = useFormRendererActions();
	const isRequired = useFieldRequired({ fieldId });
	const debouncedUpdateModifiedValue = useMemo(
		() => debounce(updateModifiedValue, 500),
		[updateModifiedValue],
	);

	const debouncedOnChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			if (fg('proforma_fix_number_input_precision')) {
				const number = sanitiseNumberInput(event.target.value);
				if (number.length === 0) {
					// Cleared field: reset local state and clear the shared value together.
					setNumberFieldValue(number);
					debouncedUpdateModifiedValue(fieldId, null);
				} else if (ALLOWED_PREFIXES.has(number)) {
					// Value-less leading states ("-", ".", "-.") let the user start typing
					// a negative or decimal number. Echo them locally so the field stays
					// editable, but leave shared state untouched until a real number
					// exists.
					setNumberFieldValue(number);
				} else if (isRepresentableNumber(number)) {
					// The value is representable without precision loss. Echo the sanitised
					// string locally (so formatted inputs like "1." or "1.50" remain
					// editable) while pushing the equivalent parsed number to shared state
					// so local and shared state stay in lockstep. Numbers too big to
					// represent without precision loss fall through to a no-op, so the
					// field rejects the keystroke and the two states never diverge.
					setNumberFieldValue(number);
					debouncedUpdateModifiedValue(fieldId, { number: parseFloat(number) });
				}
				return;
			}

			// Legacy behaviour (gate off): `setNumberFieldValue` is called
			// unconditionally, so `parseFloat` clamping a too-big number can leave
			// local and shared state inconsistent.
			const number = event.target.value
				.trim()
				.replace(/[^0-9.-]/g, '') // only allow numbers, periods, and hyphens
				.replace(/(?!^)-/g, '') // remove hyphens that are not at the start
				.replace(/(\..*)\./g, '$1'); // remove duplicate decimal points
			setNumberFieldValue(number);
			if (number.length === 0) {
				debouncedUpdateModifiedValue(fieldId, null);
			} else if (!Number.isNaN(parseFloat(number))) {
				debouncedUpdateModifiedValue(fieldId, { number: parseFloat(number) });
			}
		},
		[debouncedUpdateModifiedValue, fieldId],
	);

	return (
		<Box>
			<Textfield
				id={fieldInputId}
				value={numberFieldValue}
				isInvalid={isInvalid}
				onChange={debouncedOnChange}
				width="small"
				placeholder=""
				testId={`field-input-number-edit-${fieldId}`}
				isRequired={isRequired}
				aria-describedby={
					isInvalid && fg('jsm-accessibility-fixes-new-renderer') ? fieldErrorId : undefined
				}
			/>
		</Box>
	);
};
